import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import FundingDealStageBadge from './FundingDealStageBadge';
import FundingCommissionStatusBadge from './FundingCommissionStatusBadge';
import { useFundingRoutes } from '../../hooks/funding/useFundingRoutes';
import { fetchDealFundingCommissions } from '../../services/funding/fundingCommissionsService';

const formatCurrency = (value) => {
  if (value == null || value === '') return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
};

const DetailRow = ({ label, value }) => (
  <div className="rounded-xl border border-[var(--fg-line)] bg-[var(--fg-panel-soft)] px-3 py-2.5">
    <div className="text-[11px] font-semibold uppercase tracking-wide text-[var(--fg-muted)]">{label}</div>
    <div className="mt-1 text-sm text-[var(--fg-text)]">{value || '—'}</div>
  </div>
);

const FundingDealDetail = ({
  deal,
  agencyId,
  onClose,
  onStageChange,
  onCreateCommission,
  creatingCommission,
  saving,
}) => {
  const navigate = useNavigate();
  const { commissions: commissionsPath, lenders: lendersPath } = useFundingRoutes();
  const [linkedCommission, setLinkedCommission] = useState(null);

  useEffect(() => {
    let active = true;
    if (!deal?.id || !agencyId) {
      setLinkedCommission(null);
      return undefined;
    }
    fetchDealFundingCommissions(deal.id, agencyId)
      .then((result) => {
        if (active) setLinkedCommission(result.commissions?.[0] || null);
      })
      .catch(() => {
        if (active) setLinkedCommission(null);
      });
    return () => { active = false; };
  }, [deal?.id, agencyId]);

  if (!deal) return null;

  const isFunded = deal.stage === 'Funded';
  const showCommissionPrompt = isFunded && !linkedCommission;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          {deal.lenderId ? (
            <Link to={`${lendersPath}/${deal.lenderId}`} className="text-lg font-semibold text-[var(--fg-accent)] hover:underline">
              {deal.lenderName}
            </Link>
          ) : (
            <div className="text-lg font-semibold text-[var(--fg-text)]">{deal.lenderName}</div>
          )}
          <div className="text-sm text-[var(--fg-muted)]">
            {deal.clientName} · {deal.businessName || '—'} · {deal.productType}
          </div>
        </div>
        <FundingDealStageBadge stage={deal.stage} />
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <DetailRow label="Requested amount" value={formatCurrency(deal.requestedAmount)} />
        <DetailRow label="Approved amount" value={formatCurrency(deal.approvedAmount)} />
        <DetailRow label="Funded amount" value={formatCurrency(deal.fundedAmount)} />
        <DetailRow label="Probability" value={deal.probability != null ? `${deal.probability}% estimated` : '—'} />
        <DetailRow label="Expected commission" value={formatCurrency(deal.expectedCommission)} />
        <DetailRow label="Commission type" value={deal.commissionType} />
        <DetailRow label="Commission rate" value={deal.commissionRate != null ? String(deal.commissionRate) : '—'} />
        <DetailRow label="Close date" value={deal.closeDate} />
        <DetailRow label="Funded at" value={deal.fundedAt} />
        <DetailRow label="Clawback risk" value={deal.clawbackRisk} />
        <DetailRow label="Clawback until" value={deal.clawbackUntil} />
        <DetailRow label="Next task" value={deal.nextTask} />
        <DetailRow label="Next task due" value={deal.nextTaskDueAt} />
      </div>

      {deal.notes ? <DetailRow label="Notes" value={deal.notes} /> : null}

      {deal.submissionId ? (
        <div className="text-xs text-[var(--fg-muted)]">Linked submission: {deal.submissionId}</div>
      ) : null}

      {(deal.fundedAmount != null || deal.commissionRate != null) ? (
        <div className="rounded-xl border border-[var(--fg-line)] bg-[var(--fg-panel-soft)] px-3 py-2 text-xs text-[var(--fg-muted)]">
          Updating funded amount or commission rate on the deal will recalculate projected commission on the next commission sync.
        </div>
      ) : null}

      {linkedCommission ? (
        <div className="rounded-xl border border-[var(--fg-line)] bg-[var(--fg-panel-soft)] px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-medium text-[var(--fg-text)]">Linked commission</div>
              <div className="text-xs text-[var(--fg-muted)]">{formatCurrency(linkedCommission.commissionAmount)} · {linkedCommission.payoutStatus}</div>
            </div>
            <FundingCommissionStatusBadge status={linkedCommission.status} />
          </div>
          <Button size="sm" variant="outline" className="mt-3 rounded-xl" onClick={() => navigate(commissionsPath)}>
            Open Commissions
          </Button>
        </div>
      ) : null}

      {showCommissionPrompt ? (
        <div className="rounded-xl border border-[var(--fg-line)] bg-[var(--fg-panel-soft)] px-4 py-3">
          <div className="text-sm font-medium text-[var(--fg-text)]">Funded deal ready for commission tracking</div>
          <p className="mt-1 text-xs text-[var(--fg-muted)]">
            Create a commission record to track earned or pending payout status.
          </p>
          <Button
            size="sm"
            className="mt-3 rounded-xl bg-[var(--fg-accent)] hover:bg-[#126b4f]"
            disabled={creatingCommission}
            onClick={() => onCreateCommission?.(deal)}
          >
            {creatingCommission ? 'Creating...' : 'Create Commission'}
          </Button>
        </div>
      ) : null}

      {onStageChange ? (
        <div className="flex flex-wrap gap-2">
          {['Under Review', 'Approved', 'Contract Sent', 'Funded', 'Declined', 'Clawback Risk'].map((stage) => (
            <button
              key={stage}
              type="button"
              disabled={saving || deal.stage === stage}
              onClick={() => onStageChange(deal, stage)}
              className="rounded-xl border border-[var(--fg-line)] px-3 py-1.5 text-xs text-[var(--fg-muted)] hover:bg-[var(--fg-panel-soft)] disabled:opacity-40"
            >
              Move to {stage}
            </button>
          ))}
        </div>
      ) : null}

      {onClose ? (
        <button type="button" onClick={onClose} className="rounded-xl border border-[var(--fg-line)] px-4 py-2 text-sm text-[var(--fg-muted)]">
          Close
        </button>
      ) : null}
    </div>
  );
};

export default FundingDealDetail;
